import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { SupplementalFormType } from '@verifiedit/form-generator'
import { RouteLocationNormalized } from 'vue-router'
import stripCachedDocuments from '@/utilities/cachedDocuments'

export type SupplementalParent = 'induction' | 'startPermit'

export const useSupplementalFormStore = defineStore(
  'supplementalForm',
  () => {
    /** state */
    const forms = ref<
      Record<
        SupplementalParent,
        {
          forms: SupplementalFormType[]
          meta: Record<string, unknown> | undefined
        }
      >
    >({
      induction: {
        forms: [],
        meta: undefined,
      },
      startPermit: {
        forms: [],
        meta: undefined,
      },
    })
    const currentSupplementalForm = ref<SupplementalFormType | null>(null)
    const currentParent = ref<SupplementalParent | null>(null)
    const routeFrom = ref<RouteLocationNormalized | null>(null)
    const routeTo = ref<RouteLocationNormalized | null>(null)

    /** getters */
    const currentMeta = computed<Record<string, unknown> | undefined>(() => {
      if (!currentParent.value) {
        return undefined
      }
      return forms.value[currentParent.value].meta
    })

    /** actions */
    function setSupplementalForms(
      parent: SupplementalParent,
      value: SupplementalFormType[],
      meta?: Record<string, unknown>,
    ): void {
      forms.value[parent].forms = value
      forms.value[parent].meta = meta
    }

    function addSupplementalForm(
      parent: SupplementalParent,
      value: SupplementalFormType,
    ): void {
      if (
        forms.value[parent].forms.find(
          (formInStore) => formInStore.title === value.title,
        )
      ) {
        return
      }

      forms.value[parent].forms.push(value)
    }

    function removeSupplementalForm(
      parent: SupplementalParent,
      value: SupplementalFormType,
    ): void {
      const supplemental = forms.value[parent].forms.find(
        (supplemental) => supplemental.title === value.title,
      )
      if (supplemental) {
        forms.value[parent].forms.splice(
          forms.value[parent].forms.indexOf(supplemental),
          1,
        )
      }
    }

    function setCurrentSupplementalForm(
      parent: SupplementalParent,
      value: SupplementalFormType,
    ) {
      currentParent.value = parent
      currentSupplementalForm.value = value
    }

    function setRouteFrom(value: RouteLocationNormalized) {
      routeFrom.value = value
    }

    function setRouteTo(value: RouteLocationNormalized) {
      routeTo.value = value
    }

    function resetSupplementalForm() {
      currentSupplementalForm.value = null
      currentParent.value = null
      forms.value = {
        induction: {
          forms: [],
          meta: undefined,
        },
        startPermit: {
          forms: [],
          meta: undefined,
        },
      }
      routeFrom.value = null
      routeTo.value = null
    }
    return {
      addSupplementalForm,
      currentMeta,
      currentParent,
      currentSupplementalForm,
      forms,
      removeSupplementalForm,
      resetSupplementalForm,
      routeFrom,
      routeTo,
      setCurrentSupplementalForm,
      setRouteFrom,
      setRouteTo,
      setSupplementalForms,
    }
  },
  {
    persist: {
      afterHydrate: (context) =>
        migrateCachedSupplementalForms(context.store.$state),
    },
  },
)

/**
 * Bring a cached store up to the shape this version reads: the pre-parent `forms` array becomes
 * a record keyed by parent, and every document a cached component claims to hold is dropped.
 *
 * The documents go because they are attached freshly on each load — a persisted one is either a
 * previous version's shape or a component caught half-populated, and any object url in it died
 * with the session that made it (see `stripCachedDocuments`).
 */
export function migrateCachedSupplementalForms(state: {
  currentParent?: unknown
  currentSupplementalForm?: unknown
  forms?: unknown
}): void {
  if (Array.isArray(state.forms)) {
    const oldForms = state.forms
    state.forms = {
      induction: {
        forms: oldForms,
        meta: undefined,
      },
      startPermit: {
        forms: [],
        meta: undefined,
      },
    }
  }

  if (state.currentParent === undefined) {
    state.currentParent = null
  }

  stripCachedDocuments(state.forms)
  stripCachedDocuments(state.currentSupplementalForm)
}
